import { useMemo, useState } from 'react';
import { ScheduleQueries } from '@frontend/api-schedule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { InfinitePaginatedList } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { useContactPanelShallowStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Button, ContentLoader, styles, useAlert, useDebouncedValue, useFormField } from '@frontend/design-system';
import { WaitlistItem } from '../../components/waitlist-item';
import { WaitListItemFloatingActions } from '../../components/waitlist-item-floating-actions';
import { useCheckCanSendMessages, useGetWaitlistInfiniteQueryWithCustomData, useLocationTimezone } from '../../hooks';
import { useWaitlistShallowStore } from '../../stores';
import { EmptyListIllustration } from '../tray-content/components/empty-list-illustration';
import { SpinnerLoader } from '../tray-content/components/spinning-loader';
import { AddToListButton } from './add-to-list-button';
import { useScrollToTopOnNewPerson } from './use-scroll-to-top-on-new-person';
import { WaitListHeader } from './waitlist-header';

type Props = {
  locationId: string;
  hasAccordionWrapper?: boolean;
  height?: number | string;
};

const HEADER_FOOTER_HEIGHT = 92 + 80; // 92px for top bar, 80px for footer

const useListSelectionStore = (locationId: string) => {
  const { selectedPersonIds, selectedLocationId, makeSetSelectedPersonIds } = useWaitlistShallowStore(
    'selectedPersonIds',
    'selectedLocationId',
    'makeSetSelectedPersonIds'
  );
  const setSelectedPersonIds = makeSetSelectedPersonIds(locationId);
  return {
    selectedPersonIds: locationId === selectedLocationId ? selectedPersonIds : [],
    setSelectedPersonIds,
  };
};

export const TrayContent = ({ locationId, hasAccordionWrapper = false, height }: Props) => {
  const { t } = useTranslation('schedule-quick-fill');
  const alert = useAlert();
  const { formatDateAndTimeByLocation } = useLocationTimezone();
  const { setPersonId } = useContactPanelShallowStore('setPersonId');
  const { canSendMessages } = useCheckCanSendMessages();
  const { selectedPersonIds, setSelectedPersonIds } = useListSelectionStore(locationId);
  const { currentStep, setCurrentStep } = useWaitlistShallowStore('currentStep', 'setCurrentStep');

  const searchField = useFormField({ type: 'text' });
  const debouncedSearchTerm = useDebouncedValue(searchField.value);
  const [editingPersonId, setEditingPersonId] = useState<string>('');

  const deleteQuery = ScheduleQueries.useMutateDeleteQuickFillRecipient(locationId);
  const updateQuery = ScheduleQueries.useMutateUpdateQuickFillRecipient();
  const { infiniteQuery, isEmptyList, totalCount, optedInPersonIds, firstPersonId } =
    useGetWaitlistInfiniteQueryWithCustomData({
      locationId,
      search: debouncedSearchTerm,
    });
  const isAllSelected = !!optedInPersonIds.length && selectedPersonIds.length === optedInPersonIds.length;

  const { paginatedListRef, newPersonIdRef } = useScrollToTopOnNewPerson(firstPersonId);

  const formatDateAndTime = useMemo(
    () => formatDateAndTimeByLocation(locationId),
    [formatDateAndTimeByLocation, locationId]
  );

  // Only show loader for component for initial list load, not for searched or subsequent loads
  if (infiniteQuery.isFetching && isEmptyList && !debouncedSearchTerm) {
    return <SpinnerLoader height={hasAccordionWrapper ? 260 : '100%'} />;
  }

  if (isEmptyList && !debouncedSearchTerm) {
    return (
      <section css={[styles.flexCenter, { flexDirection: 'column' }]}>
        <EmptyListIllustration imageSize={300} />
        <AddToListButton locationId={locationId} show />
      </section>
    );
  }

  const handleClearSelection = () => setSelectedPersonIds([]);
  const handleSelectAllToggle = () => {
    setSelectedPersonIds(isAllSelected ? [] : optedInPersonIds);
  };
  const handleSingleSelectToggle = (personId: string) => () => {
    const isAlreadySelected = selectedPersonIds.includes(personId);
    const newPersonIds = isAlreadySelected
      ? selectedPersonIds.filter((id) => id !== personId)
      : [...selectedPersonIds, personId];
    setSelectedPersonIds(newPersonIds);
    SchedulerV3.trackPendoEvent('waitlist_entry_selection_changed', {
      locationId,
      personId,
      selectionStatus: isAlreadySelected ? 'unselected' : 'selected',
    });
  };
  const handleSaveNotes = async (newNotes: string, oldNote?: string) => {
    if (!editingPersonId) return;
    await updateQuery
      .mutateAsync({
        personId: editingPersonId,
        note: newNotes,
        locationId: locationId,
      })
      .then(() => {
        alert.success(t('Waitlist Recipient updated successfully'));
        SchedulerV3.trackPendoEvent('waitlist_entry_note_updated', {
          locationId,
          personId: editingPersonId,
          oldNote: oldNote || '',
          newNote: newNotes,
        });
      })
      .catch(() => alert.error(t('Error Updating Waitlist Recipient')));
  };
  const handleDelete = (personId: string) => {
    deleteQuery
      .mutateAsync({ personId })
      .then(() => {
        // If deleted person was selected, remove from selection
        if (selectedPersonIds.includes(personId)) {
          setSelectedPersonIds(selectedPersonIds.filter((id) => id !== personId));
        }
        alert.success(t('Waitlist Recipient Deleted'));
        SchedulerV3.trackPendoEvent('waitlist_entry_deleted', { locationId, personId });
      })
      .catch(() => alert.error(t('Error Deleting Waitlist Recipient')));
  };
  const handleNextClick = () => {
    if (currentStep === 'select-persons' && !!selectedPersonIds.length) {
      setCurrentStep('compose-message');
      SchedulerV3.trackPendoEvent('waitlist_next_button_clicked', { selectedPersonIds });
    }
  };

  return (
    <section css={{ overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <WaitListHeader
        hasAccordionWrapper={hasAccordionWrapper}
        locationId={locationId}
        fetchedCount={optedInPersonIds.length}
        totalCount={totalCount}
        selectedCount={selectedPersonIds.length}
        searchFieldProps={searchField}
        onSelectAllToggle={handleSelectAllToggle}
        onRefreshList={infiniteQuery.refetch}
      />
      <div css={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        <ContentLoader
          size='small'
          show={(infiniteQuery.isRefetching && !infiniteQuery.isFetchingNextPage) || deleteQuery.isLoading}
        />
        <InfinitePaginatedList
          paginatedListRef={paginatedListRef}
          emptyStateConfig={{
            type: 'quick_fill',
            header: t('No Waitlist recipients found'),
            disableGraphic: true,
            textProps: { color: 'subdued' },
          }}
          height={typeof height === 'number' ? height - HEADER_FOOTER_HEIGHT : height}
          infiniteQueryProps={infiniteQuery}
          renderListItem={({ listItem }) => {
            const personId = listItem.personId ?? '';
            const person = listItem.person;
            const optedOut = person?.receiveSms === false;
            const fullName = person ? getFullName(person) : '';
            const createdAt = listItem.createdAt || '';
            const futureApptDate = listItem.validTo || '';
            const notes = listItem.note || '';
            const selected = selectedPersonIds.includes(personId);

            return (
              <WaitListItemFloatingActions
                locationId={locationId}
                personId={personId}
                hasNotes={!!notes}
                optedOut={optedOut}
                selected={selected}
                onSelect={handleSingleSelectToggle(personId)}
                onAddEditNote={() => setEditingPersonId(personId)}
                onDelete={() => handleDelete(personId)}
              >
                <WaitlistItem
                  key={personId}
                  personName={fullName}
                  optedOut={optedOut}
                  createdAt={createdAt}
                  futureApptDate={futureApptDate}
                  notes={notes}
                  selected={selected}
                  onSelectToggle={handleSingleSelectToggle(personId)}
                  onAvatarClick={() => setPersonId(personId)}
                  editing={editingPersonId === personId}
                  selectionModeOn={!!selectedPersonIds.length}
                  highlight={listItem.personId === newPersonIdRef.current}
                  onHighlightComplete={() => (newPersonIdRef.current = undefined)}
                  isSavingNote={updateQuery.isLoading && editingPersonId === personId}
                  onEditComplete={() => setEditingPersonId('')}
                  onSaveNote={handleSaveNotes}
                  formatDateTime={formatDateAndTime}
                />
              </WaitListItemFloatingActions>
            );
          }}
        />
      </div>
      <footer css={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing(1.5), padding: theme.spacing(3) }}>
        <Button variant='secondary' disabled={!selectedPersonIds.length} onClick={handleClearSelection}>
          {t('Cancel')}
        </Button>
        <Button
          disabled={!canSendMessages || !selectedPersonIds.length}
          showLabelWhenDisabled
          hoverLabel={!canSendMessages ? t('You do not have permission to send messages') : undefined}
          onClick={handleNextClick}
        >
          {t('Next')}
        </Button>
      </footer>
    </section>
  );
};
